<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-0 md:z-50 p-0 md:p-4">
    <div class="bg-white shadow-xl w-full md:max-w-4xl md:rounded-lg md:max-h-[90vh] overflow-hidden h-full max-h-screen rounded-none flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
        <h2 class="text-xl font-semibold text-gray-800">جزئیات سفارش #{{ order?.id }}</h2>
        <button
          @click="$emit('close')"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 md:p-6 overflow-y-auto flex-1">
        <div v-if="loading" class="flex justify-center items-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brown-600"></div>
        </div>

        <div v-else-if="error" class="text-center py-8">
          <div class="text-red-600 text-lg mb-4">{{ error }}</div>
          <button
            @click="loadOrderDetails"
            class="bg-brown-600 text-white px-6 py-2 rounded-lg hover:bg-brown-700 transition-colors"
          >
            تلاش مجدد
          </button>
        </div>

        <div v-else-if="orderDetails" class="space-y-6">
          <!-- Order Info -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="bg-gray-50 p-4 rounded-lg">
              <h3 class="font-semibold text-gray-800 mb-3">اطلاعات سفارش</h3>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600">شماره سفارش:</span>
                  <span class="font-medium">#{{ orderDetails.id }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">میز:</span>
                  <span class="font-medium">{{ orderDetails.tableName }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">بخش:</span>
                  <span class="font-medium">{{ orderDetails.sectionName }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">وضعیت:</span>
                  <span
                    :class="[
                      'px-2 py-1 text-xs font-semibold rounded-full',
                      orderDetails.status === 0 ? 'bg-yellow-100 text-yellow-800' : '',
                      orderDetails.status === 1 ? 'bg-green-100 text-green-800' : '',
                      orderDetails.status === 2 ? 'bg-red-100 text-red-800' : '',
                      orderDetails.status === 3 ? 'bg-orange-100 text-orange-800' : ''
                    ]"
                  >
                    {{ getStatusText(orderDetails.status) }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">تاریخ ایجاد:</span>
                  <span class="font-medium">{{ formatDate(orderDetails.createdAt) }}</span>
                </div>
                <div v-if="orderDetails.closedAt" class="flex justify-between">
                  <span class="text-gray-600">تاریخ بسته شدن:</span>
                  <span class="font-medium">{{ formatDate(orderDetails.closedAt) }}</span>
                </div>
                <div v-if="orderDetails.notes" class="flex justify-between">
                  <span class="text-gray-600">یادداشت:</span>
                  <span class="font-medium">{{ orderDetails.notes }}</span>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 p-4 rounded-lg">
              <h3 class="font-semibold text-gray-800 mb-3">جزئیات مالی</h3>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600">مجموع سفارش:</span>
                  <span class="font-medium">{{ formatPrice(orderDetails.totalAmount) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">تعداد آیتم‌ها:</span>
                  <span class="font-medium">{{ orderDetails.orderItems?.length || 0 }} آیتم</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Items -->
          <div v-if="orderDetails.orderItems && orderDetails.orderItems.length > 0">
            <h3 class="font-semibold text-gray-800 mb-4">آیتم‌های سفارش</h3>
            <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">آیتم</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">قیمت واحد</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تعداد</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">مجموع</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="item in orderDetails.orderItems" :key="item.id">
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ item.menuItemName }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ formatPrice(item.unitPrice) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ item.quantity }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {{ formatPrice(item.totalPrice) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else class="text-center py-8 text-gray-500">
            هیچ آیتمی در این سفارش یافت نشد
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end p-4 md:p-6 border-t border-gray-200 sticky bottom-0 bg-white">
        <button
          @click="$emit('close')"
          class="bg-gray-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
        >
          بستن
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import api from '@/services/api'
import moment from 'moment-jalaali'

export default {
  name: 'OrderDetailsModal',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props) {
    const loading = ref(false)
    const error = ref(null)
    const orderDetails = ref(null)

    const loadOrderDetails = async () => {
      if (!props.order?.id) return

      try {
        loading.value = true
        error.value = null
        
        const response = await api.get(`/orders/${props.order.id}`)
        orderDetails.value = response.data
      } catch (err) {
        console.error('Error loading order details:', err)
        error.value = 'خطا در بارگذاری جزئیات سفارش'
      } finally {
        loading.value = false
      }
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fa-IR').format(price || 0) + ' تومان'
    }

    const formatDate = (dateString) => {
      const m = moment(dateString)
      return m.format('jYYYY/jMM/jDD')
    }

    const getStatusText = (status) => {
      switch (status) {
        case 0: return 'باز'
        case 1: return 'تکمیل شده'
        case 2: return 'لغو شده'
        case 3: return 'پرداخت نشده'
        default: return 'نامشخص'
      }
    }

    const getPaymentMethodText = (method) => {
      switch (method) {
        case 'cash': return 'نقد'
        case 'pos': return 'POS'
        case 'card': return 'کارت به کارت'
        case 'mixed': return 'ترکیبی'
        default: return 'بدون روش'
      }
    }

    // Watch for changes in the order prop
    watch(() => props.order, (newOrder) => {
      if (newOrder?.id) {
        loadOrderDetails()
      }
    }, { immediate: true })

    onMounted(() => {
      if (props.order?.id) {
        loadOrderDetails()
      }
    })

    return {
      loading,
      error,
      orderDetails,
      loadOrderDetails,
      formatPrice,
      formatDate,
      getStatusText,
      getPaymentMethodText
    }
  }
}
</script>

<style scoped>
/* Custom styles if needed */
</style>
